import { useState } from "react";
import { CheckCheck, ChevronDown, Plus } from "lucide-react";
import { CATEGORY_LABEL, type CareerGoal } from "../../utils/goals";
import { cn } from "../../lib/utils";

// The Goals card on Interview Prep: the user's own goals (a course, an
// article, a certification), each broken into steps. The planner puts the
// open steps on days in the calendar (blue); this card shows every step,
// when it's planned, and checks them off from here too.

const BLUE = "#5B7FA6";
/** An open goal shows this many next steps until "Show all" */
const NEXT_STEPS = 3;
/** Goals the card shows until "Show all" */
const GOALS_SHOWN = 4;

/** "Oct 30", or "Nov 30, 2025" in another year */
const shortDate = (day: string, today?: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(today && day.slice(0, 4) !== today.slice(0, 4) ? { year: "numeric" } : {}),
  });

/** "Today", "Thu", or "Oct 30" for further out */
function plannedLabel(day: string, today: string): string {
  if (day === today) return "Today";
  const daysOut = Math.round((Date.parse(`${day}T12:00:00`) - Date.parse(`${today}T12:00:00`)) / 86400000);
  if (daysOut < 7) return new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" });
  return shortDate(day);
}

function Check({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={on}
      className={cn(
        "mt-0.5 grid h-[16px] w-[16px] shrink-0 place-items-center rounded-[5px] border transition-colors",
        on ? "border-transparent text-white" : "border-melt-text3/50 bg-white hover:border-[#5B7FA6]"
      )}
      style={on ? { background: BLUE } : undefined}
    >
      {on && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 6.2 5 8.5l4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function GoalRow({
  goal,
  today,
  plannedOn,
  onToggleStep,
  onEdit,
  onDelete,
  open,
  onOpen,
}: {
  goal: CareerGoal;
  today: string;
  open: boolean;
  onOpen: (open: boolean) => void;
  plannedOn: (goalId: string, stepId: string) => string | undefined;
  onToggleStep: (stepId: string, completed: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [all, setAll] = useState(false);
  const steps = goal.lessons || [];
  const done = steps.filter((s) => s.completed).length;
  const finished = steps.length > 0 && done === steps.length;
  const late = !finished && goal.targetDate && goal.targetDate < today;
  // Folded: the next few open steps (all of them once the goal is finished)
  const left = steps.filter((s) => !s.completed);
  const shown = left.length ? left.slice(0, NEXT_STEPS) : steps.slice(-NEXT_STEPS);
  const hidden = steps.length - shown.length;
  const meta = [
    CATEGORY_LABEL[goal.category] ?? null,
    steps.length ? `${done} of ${steps.length}` : "No steps yet",
    goal.targetDate ? `${late ? "was due" : "by"} ${shortDate(goal.targetDate, today)}` : null,
  ].filter(Boolean);

  return (
    <li className="border-t border-melt-hair pt-3 first:border-t-0 first:pt-0">
      <button
        type="button"
        onClick={() => {
          onOpen(!open);
          setConfirming(false);
          setAll(false);
        }}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-2 text-left"
      >
        <span className="min-w-0">
          <span className={cn("block text-[13.5px] font-semibold leading-snug", finished && "text-melt-text3")}>{goal.title}</span>
          <span className={cn("mt-0.5 block font-plex text-[11px]", late ? "text-[#A5492E]" : "text-melt-text3")}>
            {meta.join(" · ")}
          </span>
        </span>
        <ChevronDown
          size={15}
          strokeWidth={1.75}
          className={cn("mt-0.5 shrink-0 text-melt-text3 transition-transform duration-200", open && "rotate-180")}
        />
      </button>
      {steps.length > 0 && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-melt-track">
          <div
            className="h-full rounded-full transition-[width] duration-500"
            style={{ width: `${(done / steps.length) * 100}%`, background: BLUE }}
          />
        </div>
      )}

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          {!all && done > 0 && done < steps.length && (
            <p className="mt-3 flex items-center gap-2.5 font-plex text-[11px] text-melt-text3">
              <CheckCheck size={16} strokeWidth={1.75} style={{ color: BLUE }} aria-hidden="true" />
              {done} done
            </p>
          )}
          <ul className={cn("mt-2 flex flex-col gap-2", all && "scrollbar-thin max-h-[240px] overflow-y-auto pr-1")}>
            {(all ? steps : shown).map((step) => {
              const day = step.completed ? undefined : plannedOn(goal.id, step.id);
              return (
                <li key={step.id} className="flex items-start gap-2.5">
                  <Check
                    on={step.completed}
                    onClick={() => onToggleStep(step.id, !step.completed)}
                    label={step.completed ? `${step.title}: done` : `Check off ${step.title}`}
                  />
                  <span className={cn("min-w-0 flex-1 text-[13px] leading-snug", step.completed ? "text-melt-text3 line-through" : "text-melt-text1")}>
                    {step.title}
                  </span>
                  {day && <span className="shrink-0 font-plex text-[10.5px] text-melt-text3">{plannedLabel(day, today)}</span>}
                </li>
              );
            })}
          </ul>
          {!all && hidden > 0 && (
            <button
              type="button"
              tabIndex={open ? 0 : -1}
              onClick={() => setAll(true)}
              className="mt-2 text-[12.5px] font-medium text-melt-text2 underline decoration-melt-text3/40 underline-offset-2 hover:text-melt-text1 hover:decoration-melt-text1"
            >
              Show all {steps.length} steps
            </button>
          )}
          <div className="mt-3 flex items-center gap-1 pb-0.5">
            <button
              type="button"
              tabIndex={open ? 0 : -1}
              onClick={onEdit}
              className="rounded-[8px] px-2 py-1 text-[12.5px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
            >
              Edit
            </button>
            <button
              type="button"
              tabIndex={open ? 0 : -1}
              onClick={() => (confirming ? onDelete() : setConfirming(true))}
              className={cn(
                "rounded-[8px] px-2 py-1 text-[12.5px] font-medium transition-colors",
                confirming ? "bg-[#A5492E]/[.08] text-[#A5492E]" : "text-melt-text3 hover:bg-melt-text1/[.06] hover:text-melt-text1"
              )}
            >
              {confirming ? "Delete this goal?" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

export default function PrepGoals({
  goals,
  today,
  plannedOn,
  onAdd,
  onEdit,
  onDelete,
  onToggleStep,
  className,
}: {
  goals: CareerGoal[];
  today: string;
  /** The day a step is planned for, from today on */
  plannedOn: (goalId: string, stepId: string) => string | undefined;
  onAdd: () => void;
  onEdit: (goal: CareerGoal) => void;
  onDelete: (goal: CareerGoal) => void;
  onToggleStep: (goal: CareerGoal, stepId: string, completed: boolean) => void;
  className?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  // Unfinished first, then by target date
  const sorted = [...goals].sort((a, b) => {
    const left = (g: CareerGoal) => (g.lessons || []).some((s) => !s.completed) || !(g.lessons || []).length;
    if (left(a) !== left(b)) return left(a) ? -1 : 1;
    return (a.targetDate ?? "9999").localeCompare(b.targetDate ?? "9999");
  });

  const shown = all ? sorted : sorted.slice(0, GOALS_SHOWN);

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold">
          <i className="h-2.5 w-2.5 rounded-[3px] bg-[#DCE5F0]" aria-hidden="true" />
          Goals
        </h2>
        <button
          type="button"
          onClick={onAdd}
          aria-label="Add a goal"
          title="Add a goal"
          className="grid h-7 w-7 place-items-center rounded-[8px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
        >
          <Plus size={16} strokeWidth={1.75} />
        </button>
      </div>
      {sorted.length === 0 ? (
        <p className="mt-2 text-[12.5px] leading-[1.5] text-melt-text2">
          A course, an article to write, a certification. Break it into steps and they’re planned in with your prep.{" "}
          <button type="button" onClick={onAdd} className="font-medium text-melt-text1 underline decoration-melt-text3/40 underline-offset-2 hover:decoration-melt-text1">
            Add a goal
          </button>
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-3">
          {shown.map((goal) => (
            <GoalRow
              key={goal.id}
              goal={goal}
              today={today}
              open={openId === goal.id}
              onOpen={(open) => setOpenId(open ? goal.id : null)}
              plannedOn={plannedOn}
              onToggleStep={(stepId, completed) => onToggleStep(goal, stepId, completed)}
              onEdit={() => onEdit(goal)}
              onDelete={() => onDelete(goal)}
            />
          ))}
        </ul>
      )}
      {sorted.length > GOALS_SHOWN && (
        <button
          type="button"
          onClick={() => {
            setAll(!all);
            // The open goal folds away with the rest
            if (all && !sorted.slice(0, GOALS_SHOWN).some((g) => g.id === openId)) setOpenId(null);
          }}
          className="mt-3 border-t border-melt-hair pt-3 w-full text-left text-[12.5px] font-medium text-melt-text2 transition-colors hover:text-melt-text1"
        >
          {all ? "Show fewer" : `Show all ${sorted.length} goals`}
        </button>
      )}
    </div>
  );
}
